import { MdShoppingBasket } from 'react-icons/md'
import useBasketData from '../../../hooks/useBasketData'
import { useI18n } from '../../../provider/i18n'
import { Blue600 } from '../../../lib/colors'
import { useNavigate } from 'react-router'
import { useCallback, useContext, useMemo } from 'react'
import { FieldErrors } from 'react-hook-form'
import { BasketForm } from '../NewBasket'
import Przelewy24Logo from '../../../assets/przelewy24_logo.svg'
import { AuthContext } from '../../../provider/auth'
import { getP24TransactionPanelUrl } from '../../../lib/p24'
import { useAddOrderMutation } from '../../../generated/urql.client'
import messages from '../../../lib/messages'
import { TailSpin } from 'react-loader-spinner'

const scrollToElement = (element: any) => {
  if ('getBoundingClientRect' in element) {
    const headerOffset = 80
    const y =
      element.getBoundingClientRect().top + window.pageYOffset - headerOffset
    window.scrollTo({ top: y, behavior: 'smooth' })
  } else {
    element.focus()
  }
}

const scrollToSection = (id: string) => {
  const section = document.getElementById(id)
  if (!section) {
    return
  }
  scrollToElement(section)
}

const scrollToFirstError = (errors: Omit<FieldErrors<BasketForm>, 'root'>) => {
  const errorValues = Object.values(errors)
  if (errorValues.length > 0) {
    // @ts-ignore
    scrollToElement(errorValues[0].ref)
  }
}

const Summary = () => {
  const i18n = useI18n()
  const navigate = useNavigate()
  const [{ fetching: isAddOrderLoading }, addOrder] = useAddOrderMutation()
  const { isLogged } = useContext(AuthContext)
  const {
    orderCost,
    tests,
    shippingTests,
    shippingData,
    consultations,
    trainings,
    address,
    discountCodes,
    incompleteProfiles,
    form,
    clear,
  } = useBasketData()
  const { handleSubmit } = form

  const allProfilesComplete = useMemo(
    () => !incompleteProfiles.length,
    [incompleteProfiles]
  )
  // const isProfileComplete = !!profile?.address

  // const isPayAndOrderDisabled = useMemo(
  //   () =>
  //     !isProfileComplete ||
  //     !allProfilesComplete ||
  //     !orderCost ||
  //     orderCostFetching,
  //   [orderCost, allProfilesComplete, isProfileComplete, orderCostFetching]
  // )

  const doAddOrder = useCallback(
    async (form: BasketForm) => {
      const testWithoutProfile = tests.find(({ profile }) => !profile)
      if (testWithoutProfile) {
        messages.addError(
          i18n.t('screens.basket.generalError.profileNotAssigned')
        )
        scrollToSection('tests-section')
        return
      }

      const shippingTestsWithoutProfile = shippingTests.find(
        ({ profile }) => !profile
      )
      if (shippingTestsWithoutProfile) {
        messages.addError(
          i18n.t('screens.basket.generalError.profileNotAssigned')
        )
        scrollToSection('shipping-section')
        return
      }

      const consultationWithoutProfile = consultations.find(
        ({ profile }) => !profile
      )
      if (consultationWithoutProfile) {
        messages.addError(
          i18n.t('screens.basket.generalError.profileNotAssigned')
        )
        scrollToSection('consultations-section')
        return
      }

      if (!consultations.every((c) => !!c.consultationSlot)) {
        messages.addError(
          i18n.t('screens.basket.generalError.consultationSlots')
        )
        scrollToSection('consultations-section')
        return
      }

      if (!allProfilesComplete) {
        messages.addError(
          i18n.t('screens.basket.generalError.profileIncomplete')
        )
        scrollToSection('profiles-section')
        return
      }

      const collectionType = form.collectionType || 'home'

      if (tests.length > 0) {
        if (collectionType === 'home') {
          if (!address || !address.address) {
            messages.addError(i18n.t('screens.basket.generalError.missingAddress'))
            return
          }
          if (!address.postalCode) {
            messages.addError(i18n.t('screens.basket.generalError.missingPostalCode'))
            return
          }
          if (!address.city) {
            messages.addError(i18n.t('screens.basket.generalError.missingCity'))
            return
          }
          if (!address.phoneNumber) {
            messages.addError(i18n.t('screens.basket.generalError.missingPhoneNumber'))
            return
          }
          if (!address.location) {
            messages.addError(i18n.t('screens.basket.generalError.missingLocation'))
            return
          }
        } else {
          if (!address?.phoneNumber) {
            messages.addError(i18n.t('screens.basket.generalError.missingPhoneNumber'))
            return
          }
        }
      }

      if (shippingTests.length && !shippingData?.point?.name) {
        messages.addError(
          i18n.t('screens.basket.sections.shipping.errors.pickupPointRequired')
        )
        scrollToSection('shipping-section')
        return
      }

      if (tests.length > 0) {
        if (collectionType === 'home') {
          if (!form.collectionDate || !new Date(form.collectionDate).getTime()) {
            messages.addError(
              i18n.t('screens.basket.generalError.collectionDateInvalid')
            )
            scrollToSection('tests-section')
            return
          }
        } else {
          if (!form.collectionPointId) {
            messages.addError(
              i18n.t('screens.basket.generalError.collectionPointOrSlotMissing')
            )
            scrollToSection('tests-section')
            return
          }
          if (!form.isVoucher && !form.collectionSlotId) {
            messages.addError(
              i18n.t('screens.basket.generalError.collectionPointOrSlotMissing')
            )
            scrollToSection('tests-section')
            return
          }
        }
      }

      const { data: orderInsertOutput, error: orderInsertError } =
        await addOrder({
          collections: tests.length
            ? [
                {
                  tests: tests.map(({ test, profile }) => ({
                    testId: test.id,
                    profile: {
                      firstName: profile?.firstName || '',
                      lastName: profile?.lastName || '',
                      identity: profile?.identity || '',
                      gender: profile?.gender || '-',
                    },
                  })),
                  ...(collectionType === 'stationary' && form.collectionPointId
                    ? {
                        collectionPointId: form.collectionPointId,
                        ...(!form.isVoucher
                          ? { collectionSlotId: form.collectionSlotId }
                          : {}),
                        isVoucher: form.isVoucher || false,
                        phoneNumber: address!.phoneNumber!,
                        clientNotes: form.clientNotes,
                      }
                    : {
                        collectionDate: new Date(
                          form.collectionDate
                        ).toISOString(),
                        address: address!.address,
                        postalCode: address!.postalCode,
                        city: address!.city,
                        phoneNumber: address!.phoneNumber!,
                        clientNotes: form.clientNotes,
                        location: address!.location!.coordinates,
                      }),
                },
              ]
            : [],
          consultations: consultations.map(
            ({ consultation, consultationSlot, profile }) => ({
              consultationId: consultation.id,
              preferredDate: consultationSlot
                ? new Date(consultationSlot.dateFrom).toISOString()
                : new Date().toISOString(),
              phoneNumber: profile?.phoneNumber || '-',
              profile: {
                firstName: profile?.firstName || '-',
                lastName: profile?.lastName || '-',
                identity: profile?.identity || '-',
                gender: profile?.gender || '-',
              },
              consultationSlotId: consultationSlot?.id,
            })
          ),

          trainings: trainings.map(({ training }) => ({
            trainingId: training.id,
          })),

          shipping: !!shippingTests.length
            ? {
                tests: shippingTests.map(
                  ({ test, profile, shouldShipToClient }) => ({
                    testId: test.id,
                    profile: {
                      firstName: profile?.firstName || '',
                      lastName: profile?.lastName || '',
                      identity: profile?.identity || '',
                      gender: profile?.gender || '-',
                    },
                    shouldShipToClient,
                  })
                ),
                providerCode: shippingData?.provider,
                pointName: shippingData?.point?.name || '',
                email: form.shippingEmail
                  ? form.shippingEmail.trim().toLowerCase()
                  : '',
                phone: form.shippingPhone
                  ? form.shippingPhone.trim().toLowerCase()
                  : '',
              }
            : undefined,

          discountCodes: discountCodes,
          clientConsent1: form.consent1,
          clientConsent3: form.consent3,
          clientConsent4: form.consent4,
          shippingConsent1: shippingTests.length ? form.shippingConsent1 : undefined,
          shippingConsent2: shippingTests.length ? form.shippingConsent2 : undefined,
        })

      if (orderInsertError) {
        if (orderInsertError.graphQLErrors.length) {
          orderInsertError.graphQLErrors.forEach((error) => {
            switch (error.extensions.code) {
              case 'consultation-slot-dubled':
                messages.addError(
                  i18n.t('screens.basket.generalError.consultationDubled')
                )
                break
              case 'consultation-slot-not-available':
                messages.addError(
                  i18n.t('screens.basket.generalError.consultationNotAvailable')
                )
                break
              case 'collection-point-test-not-supported':
                messages.addError(
                  i18n.t('screens.basket.generalError.collectionPointTestNotSupported')
                )
                break
              case 'tests-not-available-in-location':
                messages.addError(
                  i18n.t('screens.basket.generalError.testsNotAvailableInLocation')
                )
                break
              case 'global-consents-missing':
                messages.addError(
                  i18n.t('screens.basket.generalError.globalConsentsMissing')
                )
                break
              default:
                messages.addError(error.message)
                break
            }
          })
        } else {
          messages.addError(orderInsertError.message)
        }
        return
      }

      if (!orderInsertOutput?.client_order_add) {
        messages.addError(
          i18n.t('screens.basket.generalError.emptyOrderInsertResult')
        )
        return
      }

      try {
        clear()

        window.location.href = getP24TransactionPanelUrl(
          orderInsertOutput.client_order_add.paymentToken
        )
      } catch (error: any) {
        messages.addError(error.message)
      }
    },
    [
      address,
      allProfilesComplete,
      tests,
      shippingData,
      shippingTests,
      consultations,
      trainings,
      discountCodes,
      i18n,
      clear,
    ]
  )

  return (
    <div className="flex flex-col gap-2">
      <span className="text-lg sm:text-xl font-bold">
        {i18n.t('screens.basket.sections.summary.title')}
      </span>
      <div
        className={
          'flex flex-col sm:flex-row gap-4 justify-between bg-white border-b border-b-border border-opacity-20 p-4 sm:p-8 rounded-lg'
        }
      >
        <div className="flex flex-col justify-center items-center gap-4 text-base text-blue-700">
          {isLogged ? (
            <>
              <span>{i18n.t('screens.basket.sections.summary.payment')}</span>
              <img width={200} src={Przelewy24Logo} />
            </>
          ) : (
            <span>{i18n.t('screens.basket.sections.summary.login')}</span>
          )}
        </div>
        <div className="flex flex-col gap-4 justify-center">
          {!!orderCost && (
            <>
              <div className="flex items-center gap-2 justify-end sm:justify-normal">
                <MdShoppingBasket color={Blue600} size={24} />
                <p className="font-bold text-base sm:text-lg leading-tight">
                  {i18n.t('screens.basket.form.summary.cost', {
                    cost: Number(orderCost.cost).toFixed(2),
                  })}
                </p>
              </div>
              {orderCost.costBeforeDiscount && (
                <p className="text-right font-bold text-red-700 line-through leading-tight">
                  {i18n.t('screens.basket.form.summary.costBeforeDiscount', {
                    cost: Number(orderCost.costBeforeDiscount).toFixed(2),
                  })}
                </p>
              )}
            </>
          )}
          {isLogged ? (
            <button
              className="hl-button"
              // disabled={isPayAndOrderDisabled}
              onClick={
                !isAddOrderLoading
                  ? handleSubmit(doAddOrder, scrollToFirstError)
                  : undefined
              }
            >
              <div className="flex justify-center">
                {isAddOrderLoading ? (
                  <TailSpin
                    height={24}
                    strokeWidth={4}
                    color="white"
                    wrapperClass="self-center"
                  />
                ) : (
                  i18n.t('screens.basket.form.submit')
                )}
              </div>
            </button>
          ) : (
            <button
              className="hl-button"
              onClick={() => {
                navigate(`/login?backTo=${encodeURIComponent('/basket')}`)
              }}
            >
              {i18n.t('screens.basket.form.loginRegister')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Summary
